import { motion } from "framer-motion";
import { Vault, ArrowLeftRight, Cpu, EyeOff } from "lucide-react";

const CHAPTERS = [
  {
    n: "01",
    icon: Vault,
    title: "Cold Storage Engine",
    body: "Private keys are sealed in an offline-grade enclave on your machine. They never touch a server, a cloud, or a cable you did not choose.",
    span: "md:col-span-4 lg:col-span-7",
  },
  {
    n: "02",
    icon: ArrowLeftRight,
    title: "Atomic Multi-Chain Swaps",
    body: "Move value across chains without bridges, wrappers, or middlemen taking a cut of your conviction.",
    span: "md:col-span-4 lg:col-span-5",
  },
  {
    n: "03",
    icon: Cpu,
    title: "Hardware Enclave Sync",
    body: "Pair Cyrion with your hardware devices and sign inside silicon, not software.",
    span: "md:col-span-4 lg:col-span-5",
  },
  {
    n: "04",
    icon: EyeOff,
    title: "Zero-Trace Privacy",
    body: "No analytics. No trackers. No telemetry beacons. Your balance sheet is your business — Cyrion is built to know nothing about you.",
    span: "md:col-span-4 lg:col-span-7",
  },
];

export const Manifesto = () => {
  return (
    <section id="manifesto" className="py-24 px-4 sm:px-6 lg:px-8 border-t border-white/5" data-testid="manifesto-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16"
        >
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-mint mb-4">The manifesto</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-100 max-w-2xl" data-testid="manifesto-heading">
            Four chapters. One doctrine: <span className="gold-text">absolute ownership.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6 lg:gap-8">
          {CHAPTERS.map((c, i) => (
            <motion.article
              key={c.n}
              initial={{ opacity: 0, y: 44 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 }}
              className={`${c.span} group relative glass-panel rounded-3xl p-8 sm:p-10 overflow-hidden hover:border-gold/25 transition-colors duration-500`}
              data-testid={`manifesto-chapter-${c.n}`}
            >
              <span className="absolute -top-4 right-6 font-display text-[96px] font-extrabold text-white/[0.04] group-hover:text-gold/[0.08] transition-colors duration-500 select-none">
                {c.n}
              </span>
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-mint/10 border border-mint/25 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                  <c.icon className="w-6 h-6 text-mint" />
                </div>
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/70 mb-2">Chapter {c.n}</p>
                <h3 className="font-display text-xl sm:text-2xl font-semibold text-slate-100 mb-3">{c.title}</h3>
                <p className="text-sm sm:text-base text-slate-400 leading-relaxed max-w-md">{c.body}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
